/* Carries what the AI read out of an uploaded document from the garanzia picker to the request
   form, two navigations later (garanzia -> rischio -> richiesta).

   sessionStorage rather than router state: the visitor passes through the rischio screen in
   between, and a reload or a back/forward on the way would drop router state and silently lose
   everything the document gave us. It dies with the tab, like the access session next to it. */
const PREFILL_KEY = 'cauzioni_document_prefill';

export const DOCUMENT_HINT = 'Estratto dal documento: controlla che sia corretto.';

export const storeDocumentPrefill = (extracted) => {
  if (extracted) sessionStorage.setItem(PREFILL_KEY, JSON.stringify(extracted));
};

export const readDocumentPrefill = () => {
  try {
    const raw = sessionStorage.getItem(PREFILL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const clearDocumentPrefill = () => sessionStorage.removeItem(PREFILL_KEY);

const digitsOnly = (value) => (value ? String(value).replace(/\D/g, '') : '');

const addMonths = (isoDate, months) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  date.setMonth(date.getMonth() + months);
  return date.toISOString().slice(0, 10);
};

/**
 * Maps the AI's reading of the document onto the flat form shape.
 *
 * Returns the values to apply AND the names of the fields they filled, because the form has to be
 * able to mark exactly those inputs as "came from the document, check it" — a field the visitor
 * typed themselves must not carry that note.
 *
 * Anything the document did not state is left alone rather than written as an empty string, so a
 * partial reading never blanks a field the visitor had already filled in.
 */
export const buildPrefillValues = (extracted, municipalities = [], companyTypes = []) => {
  const values = {};
  const fields = [];

  const set = (field, value) => {
    if (value === null || value === undefined || value === '') return;
    values[field] = value;
    fields.push(field);
  };

  const c = extracted?.contraente ?? {};
  const b = extracted?.beneficiario ?? {};
  const g = extracted?.garanzia ?? {};

  if (c.tipo === 'persona_giuridica') {
    set('gender', 'G');
    set('business_name', c.denominazione);
    set('vat_number', digitsOnly(c.partita_iva));

    /* Only when it is genuinely one of the options. The document says "ASSOCIAZIONE", which is
       not in the tipologia list — writing it anyway would put the select in a state the visitor
       cannot see is wrong and cannot clear by re-picking. */
    const match = companyTypes.find(
      (t) => String(t.name ?? t).toLowerCase() === String(c.forma_giuridica ?? '').toLowerCase(),
    );
    if (match) set('company_type', match.name ?? match);
  } else if (c.tipo === 'persona_fisica') {
    // gender is deliberately not set: the document says whether it is a person, never M or F.
    set('name', c.nome);
    set('surname', c.cognome);
  }

  set('fiscal_code', c.codice_fiscale);
  set('address', c.indirizzo);
  set('house_number', c.civico);
  set('postal_code', c.cap);
  set('email', c.email);
  set('pec', c.pec);
  set('phone', digitsOnly(c.telefono));

  /* The form stores the ISTAT cadastral code, which the document never contains — only a name.
     Resolved against the comune list, and matched on the province too because names repeat. */
  if (c.citta) {
    const commune = municipalities.find((m) => {
      const sameName = m.name?.toLowerCase() === String(c.citta).toLowerCase();
      if (!sameName) return false;
      if (!c.provincia) return true;
      return m.communal_territory?.car_plate_symbol?.toLowerCase()
        === String(c.provincia).toLowerCase();
    });

    if (commune) {
      set('residence_commune_code', commune.cadastral_code);
      set('residence_province_code', commune.communal_territory?.car_plate_symbol);
    }
  }

  set('beneficiary_business_name', b.denominazione);
  set('beneficiary_address', b.indirizzo);
  set('beneficiary_house_number', b.civico);
  set('beneficiary_locality', b.citta);
  set('beneficiary_province', b.provincia);
  set('beneficiary_postal_code', b.cap);
  set('beneficiary_vat_or_fiscal_code', b.partita_iva_o_codice_fiscale);
  set('beneficiary_pec', b.pec);

  set('effective_date', g.data_effetto);
  set('guaranteed_amount', g.importo_garantito != null ? String(g.importo_garantito) : '');
  set('description', [g.descrizione, g.riferimento].filter(Boolean).join(' — '));

  if (g.data_scadenza) {
    set('expiry_date', g.data_scadenza);
  } else if (g.data_effetto && g.durata_mesi) {
    /* These documents state a minimum duration rather than an end date ("validità iniziale non
       inferiore a sei mesi"). Deriving the earliest date that satisfies it beats leaving the
       field empty — it is the answer the visitor would work out by hand, and it is flagged as
       coming from the document so they can push it out. */
    set('expiry_date', addMonths(g.data_effetto, g.durata_mesi));
  }

  return { values, fields };
};
